import { LayoutTypes } from "@stormwater-software/lib-inspection-templates";
import {
  defaultTemplateRenderer,
  QuestionGroupContext,
  RenderContextValue,
} from "@stormwater-software/ui-inspection-templates";
import React, { useContext, useEffect } from "react";

/**
 * A GRID group renders as a wide <table>, which has no readable form on a phone.
 * `_InspectionQuestions.scss` turns it into a stacked card list under 768px, and
 * each answer cell needs to carry its own column header for that to make sense
 * (otherwise two Yes/No pairs are indistinguishable).
 *
 * The cells come from <Question>, not from this component, so we cannot pass the
 * header down as a prop — stamp it on as `data-label` after render instead. The
 * `grid-header-<groupId>-<i>` ids the lib puts on each <th> let us find the table
 * without wrapping the section in an extra element (sibling/child selectors in
 * this repo's SCSS depend on the current DOM shape).
 */
export const TemplateGroupLayout: RenderContextValue["GroupLayout"] = props => {
  const {
    id,
    layout_type: layoutType,
    table_headers: headers = [],
  } = useContext(QuestionGroupContext);

  /** no dep array: rows come and go as questions mount, and re-stamping is a no-op */
  useEffect(() => {
    if (layoutType !== LayoutTypes.GRID || headers.length === 0) return;

    const firstHeader = document.getElementById(`grid-header-${id}-0`);
    const table = firstHeader && firstHeader.closest("table");

    if (!table) return;

    const rows = table.querySelectorAll("tbody > tr");

    for (let r = 0; r < rows.length; r++) {
      const cells = rows[r].children;

      /**
       * With a row label the leading cell is the "Row Header" column, so cell and
       * header indexes line up either way — but only when the counts agree.
       */
      if (cells.length !== headers.length) continue;

      for (let c = 0; c < cells.length; c++) {
        const cell = cells[c] as HTMLElement;
        const label = headers[c] || "";

        if (cell.getAttribute("data-label") !== label) {
          cell.setAttribute("data-label", label);
        }
      }
    }
  });

  return <defaultTemplateRenderer.GroupLayout {...props} />;
};
